import { useState } from "react";
import { featuredComments, type GuaComment } from "@/mocks/gua";

function formatCount(n: number) {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return String(n);
}

function CommentRow({ comment }: { comment: GuaComment }) {
  const [liked, setLiked] = useState(false);
  const avatarColors = ["bg-primary-100 text-primary-700", "bg-accent-100 text-accent-700", "bg-secondary-100 text-secondary-800"];
  const color = avatarColors[Number(comment.id.replace(/\D/g, "")) % avatarColors.length];

  return (
    <article className="rounded-2xl border border-background-200 bg-background-50 p-4">
      <div className="flex items-start gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${color}`}
          aria-hidden="true"
        >
          {comment.author.charAt(0)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-foreground-900">{comment.author}</span>
            <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] text-foreground-500">
              {comment.level}
            </span>
            <span className="text-xs text-foreground-400">{comment.time}</span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-foreground-700">{comment.content}</p>
          <div className="mt-3 flex items-center gap-4 text-xs text-foreground-400">
            <button
              type="button"
              onClick={() => setLiked((v) => !v)}
              className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 transition-colors ${
                liked ? "text-primary-600" : "hover:text-foreground-700"
              }`}
              aria-pressed={liked}
            >
              <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} aria-hidden="true" />
              {formatCount(comment.likes + (liked ? 1 : 0))}
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 transition-colors hover:text-foreground-700"
            >
              <i className="ri-chat-3-line" aria-hidden="true" />
              {comment.replies}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ThreadSection() {
  const [comments, setComments] = useState<GuaComment[]>(featuredComments);
  const [text, setText] = useState("");
  const [toast, setToast] = useState("");

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!text.trim()) return;
    const newOne: GuaComment = {
      id: `c-new-${Date.now()}`,
      author: "热心瓜友",
      level: "新晋围观",
      content: text.trim(),
      likes: 0,
      time: "刚刚",
      replies: 0,
    };
    setComments((c) => [newOne, ...c]);
    setText("");
    setToast("观点已发表，正被前排围观～");
    window.setTimeout(() => setToast(""), 2400);
  };

  return (
    <section id="thread" className="w-full bg-background-50 py-12 md:py-16" aria-label="瓜友热议">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent-700">
            <i className="ri-chat-3-line" aria-hidden="true" />
            本期高能围观
          </span>
          <h2 className="mt-1 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            瓜友热议 · 话题《长风渡口》大结局
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            一句台词的翻转刷屏全网，理性派与感动派各执一词。戳下方评论区，看看前排瓜友都怎么说，也来留下你的看法。
          </p>
        </div>

        <div className="mx-auto mt-10 grid max-w-5xl gap-8 lg:grid-cols-5">
          {/* 议题卡片 */}
          <div className="lg:col-span-2">
            <div className="overflow-hidden rounded-3xl border border-background-200 bg-background-50">
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20still%20of%20two%20people%20facing%20each%20other%20across%20a%20paper%20boat%20on%20a%20misty%20river%20at%20dusk%2C%20coral%20orange%20and%20amber%20lighting%2C%20emotional%20drama%20series%20finale%20mood%2C%20soft%20glow%2C%20editorial%20film%20aesthetic%2C%20high%20detail%20no%20text&width=960&height=720&seq=thread-cover-01&orientation=landscape"
                  alt="电视剧《长风渡口》大结局名场面画面"
                  className="h-full w-full object-cover object-top"
                />
                <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-foreground-950/75 px-3 py-1 text-xs font-semibold text-background-50 backdrop-blur-sm">
                  <i className="ri-video-line" aria-hidden="true" /> 影视综艺
                </span>
              </div>
              <div className="p-5">
                <div className="flex flex-wrap gap-1.5">
                  {["大结局", "台词反转", "名场面"].map((t) => (
                    <span key={t} className="rounded-full bg-accent-100 px-2.5 py-1 text-xs text-accent-800">
                      # {t}
                    </span>
                  ))}
                </div>
                <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
                  大结局一句台词掀翻评论区，编剧深夜长文回应
                </h3>
                <ul className="mt-4 space-y-2.5 text-sm text-foreground-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-double-line mt-0.5 text-accent-600" aria-hidden="true" />
                    讨论 1.28万 · 投票 8921 · 阅读 230万
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-time-line mt-0.5 text-secondary-600" aria-hidden="true" />
                    热帖持续发酵中，每10分钟刷新前排
                  </li>
                </ul>
                <a
                  href="#hot"
                  className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  返回热帖列表
                  <i className="ri-arrow-right-up-line" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>

          {/* 评论区 */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-background-200 bg-background-100/70 p-4 md:p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-chat-smile-2-line text-lg" aria-hidden="true" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground-900">加入这场讨论</p>
                  <p className="text-xs text-foreground-500">观点自由表达，请文明吃瓜、就事论事</p>
                </div>
              </div>
              <form onSubmit={submit} className="mt-4">
                <label htmlFor="reply" className="sr-only">
                  你的观点
                </label>
                <textarea
                  id="reply"
                  name="reply"
                  value={text}
                  maxLength={500}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="看完这个瓜，你的观点是……"
                  className="min-h-[92px] w-full resize-none rounded-xl border border-background-200 bg-background-50 p-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
                />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-foreground-400">{text.length} / 500</span>
                  <button
                    type="submit"
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!text.trim()}
                  >
                    发表观点
                    <i className="ri-send-plane-line" aria-hidden="true" />
                  </button>
                </div>
              </form>
              {toast && (
                <p
                  role="status"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-xs font-medium text-accent-800"
                >
                  <i className="ri-checkbox-circle-fill" aria-hidden="true" />
                  {toast}
                </p>
              )}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <h3 className="text-sm font-bold text-foreground-900">
                前排热议
                <span className="ml-2 text-xs font-normal text-foreground-400">{comments.length} 条</span>
              </h3>
            </div>
            <div className="mt-3 flex flex-col gap-3">
              {comments.map((c) => (
                <CommentRow key={c.id} comment={c} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}